import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, Users, Stethoscope, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalConsultations: 0,
    totalRevenue: 0,
    lowStockCount: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const StatCard = ({ title, value, icon: Icon, color, testId }) => (
    <div className="card p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium" style={{ color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
          </p>
          <p className="text-3xl font-bold mt-2" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {value}
          </p>
        </div>
        <div
          className="p-4 rounded-xl"
          style={{ backgroundColor: color }}
        >
          <Icon size={28} style={{ color: '#990099' }} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg" style={{ color: '#6B7280' }}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>Welcome to Jantikar Ayurveda Clinic Management System</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={stats.totalPatients}
          icon={Users}
          color="#F5E6F5"
          testId="stat-total-patients"
        />
        <StatCard
          title="Consultations"
          value={stats.totalConsultations}
          icon={Stethoscope}
          color="#F5E6F5"
          testId="stat-total-consultations"
        />
        <StatCard
          title="Revenue"
          value={`₹${stats.totalRevenue.toFixed(2)}`}
          icon={TrendingUp}
          color="#F5E6F5"
          testId="stat-total-revenue"
        />
        <StatCard
          title="Low Stock Alerts"
          value={stats.lowStockCount}
          icon={AlertTriangle}
          color={stats.lowStockCount > 0 ? '#FEF3C7' : '#F5E6F5'}
          testId="stat-low-stock"
        />
      </div>

      {stats.lowStockCount > 0 && (
        <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }} data-testid="low-stock-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle size={24} style={{ color: '#D97706' }} />
            <div>
              <p className="font-medium" style={{ color: '#92400E' }}>Low Stock Alert</p>
              <p className="text-sm mt-1" style={{ color: '#92400E' }}>
                {stats.lowStockCount} medicine(s) are running low on stock. Please reorder soon.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 card p-8">
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            className="primary-btn p-4 text-left"
            onClick={() => window.location.href = '/patients'}
            data-testid="quick-add-patient"
          >
            <Users size={20} className="mb-2" />
            <p className="font-medium">Add New Patient</p>
          </button>
          <button
            className="primary-btn p-4 text-left"
            onClick={() => window.location.href = '/consultations'}
            data-testid="quick-new-consultation"
          >
            <Stethoscope size={20} className="mb-2" />
            <p className="font-medium">New Consultation</p>
          </button>
          <button
            className="primary-btn p-4 text-left"
            onClick={() => window.location.href = '/billing'}
            data-testid="quick-create-bill"
          >
            <TrendingUp size={20} className="mb-2" />
            <p className="font-medium">Create Bill</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
